<template>
  <div>
    <div class="pagination">
      <div
        class="pagination-button"
        :class="{'disabled': isDisabledPrev}"
        @click="() => isDisabledPrev ? null : changePage(currentPage - 1)"
      >
        <div class="pagination__page --prev">
          <img src="~/assets/img/svg/common/arrow.svg" />
        </div>
      </div>

      <template
        v-for="(page) in pages"
        :key="page.title"
      >
        <a
          class="pagination__page"
          :class="{
          'disabled': Boolean(page.disabled),
          'selected': Boolean(page.page === currentPage)
        }"
          :href="Boolean(page.page) ? _getPageLikn(page.page) : ''"
          @click.prevent="changePage(page.page)"
        >
          {{ page.title }}
        </a>
      </template>

      <div
        class="pagination-button"
        :class="{'disabled': isDisabledNext}"
        @click="() => isDisabledNext ? null : changePage(currentPage + 1)"
      >
        <div class="pagination__page --next">
          <img src="~/assets/img/svg/common/arrow.svg" />
        </div>
      </div>
    </div>
    <template v-if="isShowMore">
      <div class="pagination__more">
        <button
          class="btn primary"
          :disabled="isDisabledNext"
          @click="loadMore"
        >Показать еще</button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, toRefs, useNuxtApp, useRoute, watch } from '#imports';

interface IPaginationProps {
	totalPages: number;
	currentPage: number;
  isShowMore?: boolean;
}

interface IPages {
	title: string;
	disabled?: boolean;
	page?: number;
}

const nuxtApp = useNuxtApp();
const $route = useRoute();
const pages = ref<IPages[]>([]);

const props = withDefaults(defineProps<IPaginationProps>(), {
  currentPage: 1,
  totalPages: 1,
  isShowMore: false
});

const { currentPage, totalPages } = toRefs(props);
const emits = defineEmits();

const isDisabledPrev = computed(() => props.currentPage <= 1);
const isDisabledNext = computed(() => props.currentPage >= props.totalPages);

const _getPageLikn = (pageNumber: number | undefined) => {
  let host = 'https://';
  host += process.server ? nuxtApp.ssrContext!.event.node.req.headers.host : window.location.host;

  if (!pageNumber) {
    return host;
  }

  host += $route.fullPath.includes('page=')
    ? $route.fullPath.replace(/page=\d+/, `page=${pageNumber}`)
    : `${$route.fullPath}${($route.fullPath || '').includes('?') ? '&' : '?'}` + `page=${pageNumber}`;

  return host;
};

const initPages = () => {
  const page = props.currentPage;
  const siblingCount = 1;
  const boundaryCount = 1;
  const count = props.totalPages;

  const range = function range (start: number, end: number) {
    const length = end - start + 1;
    return Array.from(
      {
        length
      },
      (_, i) => `${start + i}`
    );
  };

  const startPages = range(1, Math.min(boundaryCount, count));

  const endPages = range(
    Math.max(count - boundaryCount + 1, boundaryCount + 1),
    count
  );

  const siblingsStart = Math.max(
    Math.min(
      page - siblingCount,
      count - boundaryCount - siblingCount * 2 - 1
    ),
    boundaryCount + 2
  );

  const siblingsEnd = Math.min(
    Math.max(page + siblingCount, boundaryCount + siblingCount * 2 + 2),
    +endPages[0] - 2
  );

  const items: string[] = ['']
    .concat(
      startPages,
      siblingsStart > boundaryCount + 2
        ? ['start-ellipsis']
        : boundaryCount + 1 < count - boundaryCount
          ? [`${boundaryCount + 1}`]
          : [],
      range(siblingsStart, siblingsEnd),
      siblingsEnd < count - boundaryCount - 1
        ? ['end-ellipsis']
        : count - boundaryCount > boundaryCount
          ? [`${count - boundaryCount}`]
          : [],
      endPages
    )
    .filter(Boolean);

  pages.value = items.map((item) => {
    if (!['start-ellipsis', 'end-ellipsis'].includes(item)) {
      return { title: item, page: +item };
    }

    return { title: '...', disabled: true };
  });
};

const changePage = (newPage: number | undefined) => {
  if (!newPage) {
    emits('change', 1);
    return;
  }

  if (newPage < 1 || newPage > props.totalPages || newPage === props.currentPage) {
    return;
  }
  emits('change', newPage);
};

watch(totalPages, () => {
  initPages();
});

watch(currentPage, () => {
  initPages();
});

initPages();

const loadMore = () => {
  emits('loadMore', currentPage.value + 1);
}

</script>

<style lang="scss" scoped>
.pagination {
	display: flex;
	align-items: center;
	justify-content: center;

	& > * {
		margin-left: 8px;

		&:first-child {
			margin-left: 0;
		}
	}
}

.pagination__arrow {
}
.pagination__page {
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	height: 38px;
	min-width: 38px;
	border-radius: 12px;

	&.selected {
		background: #F4F3F1;
	}

	&.--next {
		img {
			transform: rotate(180deg);
			object-fit: contain;
		}
	}

	&:hover {
		background: #F4F3F1;
	}
}

.pagination-button.disabled {
  display: none;
  pointer-events: none;
  opacity: 0;
}

.pagination__more {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
}
</style>
